import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import {
    Checkbox,
    CircularProgress,
    Menu,
    MenuItem,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TablePagination,
    TableRow,
    Tooltip,
} from '@mui/material';
import DropzoneArea from '../ClientDetails/SubComponents/DropzoneArea';
import XtramModal, {
    XtramBadge,
    XtramButton,
    XtramCheckbox,
    XtramField,
    XtramInput,
    XtramNotice,
    XtramSearch,
    XtramSelect,
    XtramStat,
    XtramTable,
    xtramStyles as styles,
} from '../Widgets/XtramModal/XtramModal';
import ImportExportService from '../../Services/importExport.service';
import EnumsService from '../../Services/enums.service';
import Constants from '../../assests/Constants/constants';
import { useDirectionByLanguage } from '../../hooks/useDirectionByLanguage';

const ZIP_ACCEPT = { 'application/zip': ['.zip'], 'application/x-zip-compressed': ['.zip'] };

const KNOWN_ERRORS = [
    'ATZMAIT_INVALID_ZIP', 'ATZMAIT_PASSWORD_REQUIRED', 'ATZMAIT_WRONG_PASSWORD', 'ATZMAIT_NOT_A_BACKUP',
    'ATZMAIT_YEAR_NOT_IN_BACKUP', 'ATZMAIT_UNREADABLE', 'ATZMAIT_CLIENT_NOT_FOUND',
    'ATZMAIT_BUSINESS_NOT_IN_BACKUP', 'ATZMAIT_NO_ASSIGNMENTS',
];

const MATCH_TONES = {
    manual: 'info',
    caseId: 'success',
    vatNumber: 'success',
    clientId: 'success',
    taxNumber: 'success',
    ambiguous: 'warning',
};

const STATUS_TONES = {
    new: 'info',
    partial: 'warning',
    imported: 'success',
    recheck: 'warning',
};

const PL_TONES = {
    same: 'success',
    different: 'error',
    noData: 'info',
    unchecked: 'warning',
    noClient: undefined,
};

const PL_FILTERS = ['same', 'different', 'noData', 'unchecked', 'noClient'];

const plStatusOf = (pl) => {
    if (!pl) return null;
    if (!pl.hasSystemData) return 'noData';
    return pl.isSame ? 'same' : 'different';
};

/** Where a business with movements stands in the all-clients summary: its P&L status, or why it has none. */
const plKeyOf = (business, status) => {
    if (business.rowCount === 0) return null;
    if (status === 'noClient') return 'noClient';
    if (status === 'recheck') return 'unchecked';
    return plStatusOf(business.profitAndLoss) ?? 'noData';
};

const formatAmount = (value) => (value == null || value === ''
    ? '—'
    : Number(value).toLocaleString('he-IL', { minimumFractionDigits: 2, maximumFractionDigits: 2 }));

const formatDate = (value) => {
    if (!value) return '—';
    const [y, m, d] = String(value).slice(0, 10).split('-');
    return d && m && y ? `${d}/${m}/${y}` : String(value);
};

const errorKey = (err) => String(err?.message || '').trim().replace(/^"|"$/g, '');

const toEditable = (sc) => ({
    ...sc,
    sortCodeVatType: sc.sortCodeVatType == null ? null : String(sc.sortCodeVatType),
    sortCodeCategory: sc.sortCodeGroup ?? null,
    participatesInProfitAndLoss: sc.participatesInProfitAndLoss ?? null,
    vat: sc.vat ?? null,
    businessNumbers: sc.businessNumbers ?? [],
});

/** The clients chosen by hand: those the server matched as manual and those changed since the read. */
const manualAssignmentsOf = (businesses, assignments) => businesses
    .filter((b) => b.matchType === 'manual' || (assignments[b.businessNumber] ?? null) !== (b.caseId ?? null))
    .map((b) => ({ businessNumber: b.businessNumber, caseId: assignments[b.businessNumber] ?? '' }));

const CHECK_DELAY_MS = 700;

/** What the profit and loss after the import is simulated with: the missing sort codes as set, by name. */
const toSetting = (sc) => ({
    sortCodeName: sc.sortCodeName?.trim() ?? '',
    sortCodeGroup: sc.sortCodeCategory ?? null,
    sortCodeVatType: sc.sortCodeVatType == null ? null : parseInt(sc.sortCodeVatType, 10),
    vat: sc.vat ?? null,
    participatesInProfitAndLoss: sc.participatesInProfitAndLoss ?? null,
});

const simulationKey = (codes, importDuplicates) => JSON.stringify([codes.map(toSetting), !!importDuplicates]);

const clientLabel = (client) => (client ? `${client.name || ''} (${client.caseId})` : '');

const businessLabel = (b) => [b.businessNumber, b.name, b.dealerNumber].filter(Boolean).join(' · ');

const filterClients = (options, { inputValue }) => {
    const term = inputValue.trim().toLowerCase();
    const found = term
        ? options.filter((c) => [c.name, c.caseId, c.vatNumber].some((v) => String(v ?? '').toLowerCase().includes(term)))
        : options;
    return found.slice(0, 100);
};

/**
 * Imports the תקבולים/תשלומים of an עצמאית backup zip like the Atzmait CSV import.
 * With caseId it imports one client (the business matched to it, or one the user picks); without it, every
 * business of the backup into the client it matches by dealer number or the one the user picks for it.
 */
const AtzmaitBackupImportModal = ({ open, onClose, accountantId, token, caseId = null, clientName = '', onImported }) => {
    const { t, i18n } = useTranslation();
    const direction = useDirectionByLanguage(i18n);
    const tb = (key, options) => t(`atzmaitBackup.${key}`, options);

    const [scope, setScope] = useState(caseId ? 'client' : 'all');
    const single = !!caseId && scope === 'client';

    const [tab, setTab] = useState('file');
    const [file, setFile] = useState(null);
    const [password, setPassword] = useState('');
    const [year, setYear] = useState(null);
    const [preview, setPreview] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [results, setResults] = useState(null);

    const [missing, setMissing] = useState([]);
    const [selectedMissing, setSelectedMissing] = useState([]);
    const [bulk, setBulk] = useState({});
    const [groupOptions, setGroupOptions] = useState([]);

    const [assignments, setAssignments] = useState({});
    const [selected, setSelected] = useState([]);
    const [importDuplicates, setImportDuplicates] = useState(false);

    const [businessSearch, setBusinessSearch] = useState('');
    const [hideEmpty, setHideEmpty] = useState(true);
    const [onlyUnmatched, setOnlyUnmatched] = useState(false);
    const [businessPage, setBusinessPage] = useState(0);

    const [entrySearch, setEntrySearch] = useState('');
    const [entryPage, setEntryPage] = useState(0);
    const [entryRowsPerPage, setEntryRowsPerPage] = useState(25);

    const [detail, setDetail] = useState(null);
    const [checkSearch, setCheckSearch] = useState('');
    const [checkStatus, setCheckStatus] = useState(null);
    const [checkPage, setCheckPage] = useState(0);
    const [onlyDifferent, setOnlyDifferent] = useState(false);
    const [checkPl, setCheckPl] = useState(null);
    const [plOnlyDifferent, setPlOnlyDifferent] = useState(false);
    const [variantMenu, setVariantMenu] = useState(null);
    const [simulatedWith, setSimulatedWith] = useState(null);
    const [toCheck, setToCheck] = useState([]);
    const [checking, setChecking] = useState([]);
    const assignmentsRef = useRef(assignments);
    assignmentsRef.current = assignments;

    useEffect(() => {
        if (!open || !accountantId) return;
        let cancelled = false;
        (async () => {
            try {
                const res = await EnumsService.GetSortCodesGroupByAccountantId(accountantId, token);
                const data = res?.ok ? await res.json() : [];
                if (!cancelled) {
                    setGroupOptions((data || []).map((g) => ({ value: g.sortCodeGroup, label: `${g.sortCodeGroup}` })));
                }
            } catch {
                if (!cancelled) setGroupOptions([]);
            }
        })();
        return () => { cancelled = true; };
    }, [open, accountantId, token]);

    const vatTypeOptions = useMemo(
        () => Object.entries(Constants.SortCodeVatType).map(([value, name]) => ({ value, label: t(name) })),
        [t]
    );
    const vatOptions = useMemo(
        () => (Constants.VATPercentage || []).map((v) => ({ value: v, label: `${v}%` })),
        []
    );
    const yesNoOptions = useMemo(() => [{ value: true, label: t('Yes') }, { value: false, label: t('No') }], [t]);
    const yearOptions = useMemo(() => (preview?.years ?? []).map((y) => ({ value: y, label: String(y) })), [preview]);

    const businesses = useMemo(() => preview?.businesses ?? [], [preview]);
    const clients = useMemo(() => preview?.clients ?? [], [preview]);
    const clientsByCase = useMemo(() => new Map(clients.map((c) => [c.caseId, c])), [clients]);
    const selectedBusiness = single
        ? businesses.find((b) => b.businessNumber === preview?.selectedBusinessNumber) ?? null
        : null;

    const reset = () => {
        setPreview(null);
        setResults(null);
        setError('');
        setMissing([]);
        setSelectedMissing([]);
        setBulk({});
        setAssignments({});
        setSelected([]);
        setImportDuplicates(false);
        setBusinessPage(0);
        setEntryPage(0);
        setEntrySearch('');
        setDetail(null);
        setCheckPage(0);
        setCheckStatus(null);
        setCheckPl(null);
        setOnlyDifferent(false);
        setSimulatedWith(null);
        setToCheck([]);
        setChecking([]);
        setTab('file');
    };

    const handleClose = () => {
        if (loading) return;
        reset();
        setFile(null);
        setYear(null);
        setScope(caseId ? 'client' : 'all');
        onClose?.();
    };

    const showError = (err) => {
        const key = errorKey(err);
        setError(KNOWN_ERRORS.includes(key) ? tb(`errors.${key}`) : tb('errors.generic'));
    };

    const runPreview = async ({ businessNumber = null, nextYear = year, nextScope = scope, keepChoices = true } = {}) => {
        if (!file) return;
        const forClient = !!caseId && nextScope === 'client';
        const manualAssignments = forClient || !keepChoices ? [] : manualAssignmentsOf(businesses, assignments);
        setToCheck([]);
        setLoading(true);
        setError('');
        setResults(null);
        setDetail(null);
        try {
            const res = await ImportExportService.PreviewAtzmaitBackup({
                accountantId,
                file,
                password,
                year: nextYear,
                caseId: forClient ? caseId : null,
                businessNumber: forClient ? businessNumber : null,
                assignments: manualAssignments,
                sortCodes: keepChoices ? missing.map(toSetting) : [],
                importDuplicates,
            }, token);
            const nextMissing = (res.missingSortCodes || []).map(toEditable);
            setPreview(res);
            setYear(res.year);
            setMissing(nextMissing);
            setSimulatedWith(simulationKey(nextMissing, importDuplicates));
            setSelectedMissing([]);
            setEntryPage(0);
            setBusinessPage(0);

            const nextAssignments = {};
            (res.businesses || []).forEach((b) => { if (b.caseId) nextAssignments[b.businessNumber] = b.caseId; });
            setAssignments(nextAssignments);
            setSelected((res.businesses || [])
                .filter((b) => b.caseId && b.rowCount > 0 && b.duplicateCount < b.rowCount)
                .map((b) => b.businessNumber));
            setTab((current) => (current === 'file' || current === 'results' ? (forClient ? 'data' : 'check') : current));
        } catch (err) {
            console.error('Atzmait backup preview failed:', err);
            setPreview(null);
            setTab('file');
            showError(err);
        } finally {
            setLoading(false);
        }
    };

    const handleFileDrop = (files) => {
        if (!files?.length) return;
        setFile(files[0]);
        setYear(null);
        reset();
    };

    const changeScope = (nextScope, read = false) => {
        if (loading || nextScope === scope) return;
        setScope(nextScope);
        reset();
        if (read) runPreview({ nextScope, keepChoices: false });
    };

    /* ───────── Checking a business against the system ───────── */

    const openDetail = async (business) => {
        const assigned = assignments[business.businessNumber];
        if (!assigned || !file) return;
        const simulatedFor = simulationKey(missing, importDuplicates);
        setDetail({ business, caseId: assigned, entries: [], loading: true });
        setEntrySearch('');
        setEntryPage(0);
        try {
            const res = await ImportExportService.PreviewAtzmaitBackup({
                accountantId,
                file,
                password,
                year,
                caseId: assigned,
                businessNumber: business.businessNumber,
                sortCodes: missing.map(toSetting),
                importDuplicates,
            }, token);
            const read = (res.businesses || []).find((b) => b.businessNumber === res.selectedBusinessNumber);
            setDetail((current) => (current?.business.businessNumber === business.businessNumber
                ? {
                    ...current,
                    entries: res.entries || [],
                    profitAndLoss: read?.profitAndLoss ?? null,
                    simulatedWith: simulatedFor,
                    loading: false,
                }
                : current));
        } catch (err) {
            console.error('Reading the business of the Atzmait backup failed:', err);
            toast.error(tb('errors.generic'));
            setDetail(null);
        }
    };

    /* ───────── Choosing a client by hand (all clients) ───────── */

    /** Checks the businesses against the system (and simulates their P&L) without reading the others again. */
    const checkBusinesses = async (numbers) => {
        setToCheck((prev) => prev.filter((n) => !numbers.includes(n)));
        setChecking((prev) => [...new Set([...prev, ...numbers])]);
        try {
            const res = await ImportExportService.PreviewAtzmaitBackup({
                accountantId,
                file,
                password,
                year,
                assignments: manualAssignmentsOf(businesses, assignments),
                sortCodes: missing.map(toSetting),
                importDuplicates,
                checkBusinessNumbers: numbers,
            }, token);
            const fresh = new Map((res.businesses || [])
                .filter((b) => numbers.includes(b.businessNumber) && b.checked)
                .map((b) => [b.businessNumber, b]));
            setPreview((prev) => prev && {
                ...prev,
                businesses: prev.businesses.map((b) => {
                    const checked = fresh.get(b.businessNumber);
                    return checked && checked.caseId === assignmentsRef.current[b.businessNumber] ? checked : b;
                }),
            });
        } catch (err) {
            console.error('Checking the Atzmait businesses failed:', err);
            toast.error(tb('errors.generic'));
        } finally {
            setChecking((prev) => prev.filter((n) => !numbers.includes(n)));
        }
    };
    const checkBusinessesRef = useRef(checkBusinesses);
    checkBusinessesRef.current = checkBusinesses;

    useEffect(() => {
        if (single || !preview || loading || toCheck.length === 0) return undefined;
        const timer = setTimeout(() => checkBusinessesRef.current(toCheck), CHECK_DELAY_MS);
        return () => clearTimeout(timer);
    }, [toCheck, single, preview, loading]);

    const changeClient = (business, client) => {
        const nextCaseId = client?.caseId ?? null;
        setAssignments((prev) => {
            const next = { ...prev };
            if (nextCaseId) next[business.businessNumber] = nextCaseId;
            else delete next[business.businessNumber];
            return next;
        });
        setSelected((prev) => (nextCaseId
            ? (prev.includes(business.businessNumber) || business.rowCount === 0 ? prev : [...prev, business.businessNumber])
            : prev.filter((n) => n !== business.businessNumber)));
        if (nextCaseId && business.rowCount > 0) {
            setToCheck((prev) => (prev.includes(business.businessNumber) ? prev : [...prev, business.businessNumber]));
        }
    };

    const isChecking = (b) => checking.includes(b.businessNumber) || toCheck.includes(b.businessNumber);
    const checkingCount = new Set([...checking, ...toCheck]).size;

    const renderPlBadge = (b, plKey) => {
        if (isChecking(b)) return <XtramBadge tone="info">{tb('checkingBadge')}</XtramBadge>;
        return plKey ? <XtramBadge tone={PL_TONES[plKey]}>{tb(`plStatuses.${plKey}`)}</XtramBadge> : '—';
    };

    /* ───────── Missing sort codes ───────── */

    const importedBusinessNumbers = useMemo(
        () => (single ? (selectedBusiness ? [selectedBusiness.businessNumber] : []) : selected),
        [single, selectedBusiness, selected]
    );

    const relevantMissing = useMemo(() => {
        const wanted = new Set(importedBusinessNumbers);
        return missing
            .map((sc, index) => ({ sc, index }))
            .filter(({ sc }) => sc.businessNumbers.some((n) => wanted.has(n)));
    }, [missing, importedBusinessNumbers]);

    const missingIncomplete = relevantMissing.some(({ sc }) =>
        !sc.sortCodeName?.trim() || sc.sortCodeVatType == null || sc.participatesInProfitAndLoss == null || sc.vat == null);

    const updateMissing = (index, field, value) =>
        setMissing((prev) => prev.map((sc, i) => (i === index ? { ...sc, [field]: value } : sc)));

    const applyVariant = (index, variant) => {
        setMissing((prev) => prev.map((sc, i) => (i === index
            ? {
                ...sc,
                sortCodeVatType: String(variant.sortCodeVatType),
                vat: variant.vat,
                participatesInProfitAndLoss: variant.participatesInProfitAndLoss,
            }
            : sc)));
        setVariantMenu(null);
    };

    const isVariantApplied = (sc, variant) => String(variant.sortCodeVatType) === String(sc.sortCodeVatType)
        && variant.vat === sc.vat
        && variant.participatesInProfitAndLoss === sc.participatesInProfitAndLoss;

    const applyBulk = () => {
        setMissing((prev) => prev.map((sc) => (selectedMissing.includes(sc.sortCodeId)
            ? {
                ...sc,
                ...('sortCodeVatType' in bulk ? { sortCodeVatType: bulk.sortCodeVatType } : {}),
                ...('sortCodeCategory' in bulk ? { sortCodeCategory: bulk.sortCodeCategory } : {}),
                ...('participatesInProfitAndLoss' in bulk ? { participatesInProfitAndLoss: bulk.participatesInProfitAndLoss } : {}),
                ...('vat' in bulk ? { vat: bulk.vat } : {}),
            }
            : sc)));
        setSelectedMissing([]);
        setBulk({});
    };

    const createMissingSortCodes = async () => {
        const codes = relevantMissing.map(({ sc }) => sc);
        if (codes.length === 0) return;
        const responses = await Promise.all(codes.map((sc) => EnumsService.AddSortCode(
            accountantId,
            sc.sortCodeName.trim(),
            sc.sortCodeId,
            sc.sortCodeCategory ?? null,
            null,
            null,
            parseInt(sc.sortCodeVatType, 10),
            sc.vat,
            sc.participatesInProfitAndLoss,
            null,
            null,
            0,
            token
        )));
        const failed = responses.filter((r) => !r || !r.ok).length;
        if (failed > 0) throw new Error(`${failed} sort codes failed to create`);
        toast.success(t('MissingSortCodesCreated'));
    };

    /* ───────── Commit ───────── */

    const commitAssignments = useMemo(() => {
        if (single) return selectedBusiness ? [{ businessNumber: selectedBusiness.businessNumber, caseId }] : [];
        return selected
            .filter((n) => assignments[n])
            .map((n) => ({ businessNumber: n, caseId: assignments[n] }));
    }, [single, selectedBusiness, caseId, selected, assignments]);

    const handleCommit = async () => {
        if (!file || commitAssignments.length === 0) return;
        if (missingIncomplete) {
            setTab('missing');
            return;
        }
        setLoading(true);
        setError('');
        try {
            try {
                await createMissingSortCodes();
            } catch (err) {
                console.error('Creating the missing sort codes failed:', err);
                toast.error(t('CreateSortCodesFailed'));
                return;
            }

            const res = await ImportExportService.CommitAtzmaitBackup({
                accountantId, file, password, year, assignments: commitAssignments, importDuplicates,
            }, token);
            setResults(res);
            setMissing((prev) => prev.filter((sc) => !relevantMissing.some(({ sc: done }) => done.sortCodeId === sc.sortCodeId)));
            setTab('results');
            if (res.errorCount > 0) toast.warning(tb('importedWithErrors', { count: res.processedCount, errors: res.errorCount }));
            else toast.success(tb('imported', { count: res.processedCount }));
            onImported?.(res);
        } catch (err) {
            console.error('Atzmait backup import failed:', err);
            showError(err);
            toast.error(t('ImportFailed'));
        } finally {
            setLoading(false);
        }
    };

    /* ───────── Derived table data ───────── */

    const visibleBusinesses = useMemo(() => {
        const term = businessSearch.trim().toLowerCase();
        return businesses.filter((b) => {
            if (hideEmpty && b.rowCount === 0) return false;
            if (onlyUnmatched && assignments[b.businessNumber]) return false;
            if (!term) return true;
            const client = clientsByCase.get(assignments[b.businessNumber]);
            return [b.businessNumber, b.name, b.dealerNumber, b.fileNumber, client?.name, client?.caseId]
                .some((v) => String(v ?? '').toLowerCase().includes(term));
        });
    }, [businesses, businessSearch, hideEmpty, onlyUnmatched, assignments, clientsByCase]);

    const businessRowsPerPage = 50;
    const pagedBusinesses = visibleBusinesses.slice(businessPage * businessRowsPerPage, (businessPage + 1) * businessRowsPerPage);
    const selectableVisible = visibleBusinesses.filter((b) => assignments[b.businessNumber] && b.rowCount > 0);
    const allVisibleSelected = selectableVisible.length > 0 && selectableVisible.every((b) => selected.includes(b.businessNumber));

    const statusOf = useCallback((b) => {
        const assigned = assignments[b.businessNumber];
        if (!assigned) return 'noClient';
        if (b.rowCount === 0) return 'empty';
        if (!b.checked || b.caseId !== assigned) return 'recheck';
        if (b.duplicateCount === 0) return 'new';
        return b.duplicateCount >= b.rowCount ? 'imported' : 'partial';
    }, [assignments]);

    const checkRows = useMemo(() => {
        const term = checkSearch.trim().toLowerCase();
        return businesses
            .map((b) => {
                const backupAmount = (Number(b.incomeTotal) || 0) + (Number(b.expenseTotal) || 0);
                const inSystemAmount = Number(b.duplicateAmount) || 0;
                const status = statusOf(b);
                return {
                    business: b,
                    client: clientsByCase.get(assignments[b.businessNumber]) ?? null,
                    status,
                    plStatus: ['new', 'partial', 'imported'].includes(status) ? plStatusOf(b.profitAndLoss) : null,
                    plKey: plKeyOf(b, status),
                    newCount: Math.max(0, b.rowCount - (b.duplicateCount || 0)),
                    backupAmount,
                    inSystemAmount,
                    newAmount: backupAmount - inSystemAmount,
                };
            })
            .filter((row) => {
                if (checkStatus ? row.status !== checkStatus.value : row.status === 'empty') return false;
                if (checkPl && row.plKey !== checkPl) return false;
                if (!term) return true;
                return [row.business.businessNumber, row.business.name, row.business.dealerNumber, row.client?.name, row.client?.caseId]
                    .some((v) => String(v ?? '').toLowerCase().includes(term));
            });
    }, [businesses, assignments, clientsByCase, checkSearch, checkStatus, checkPl, statusOf]);

    const plSummary = useMemo(() => {
        const counts = Object.fromEntries(PL_FILTERS.map((key) => [key, 0]));
        businesses.forEach((b) => {
            const key = plKeyOf(b, statusOf(b));
            if (key) counts[key] += 1;
        });
        return counts;
    }, [businesses, statusOf]);

    const canImport = useCallback((b) => !!assignments[b.businessNumber] && b.rowCount > 0, [assignments]);
    const checkSelectable = checkRows.map((row) => row.business).filter(canImport);

    const selectedSummary = useMemo(() => {
        const chosen = businesses.filter((b) => selected.includes(b.businessNumber) && canImport(b));
        const count = (key) => chosen.filter((b) => plKeyOf(b, statusOf(b)) === key).length;
        return { count: chosen.length, different: count('different'), unchecked: count('unchecked') };
    }, [businesses, selected, canImport, statusOf]);

    const pendingCount = useMemo(
        () => businesses.filter((b) => ['new', 'partial'].includes(statusOf(b))).length,
        [businesses, statusOf]
    );

    const checkTotals = useMemo(() => checkRows
        .filter((row) => ['new', 'partial', 'imported'].includes(row.status))
        .reduce((sum, row) => ({
            rows: sum.rows + row.business.rowCount,
            inSystem: sum.inSystem + (row.business.duplicateCount || 0),
            newRows: sum.newRows + row.newCount,
            backupAmount: sum.backupAmount + row.backupAmount,
            inSystemAmount: sum.inSystemAmount + row.inSystemAmount,
            newAmount: sum.newAmount + row.newAmount,
        }), { rows: 0, inSystem: 0, newRows: 0, backupAmount: 0, inSystemAmount: 0, newAmount: 0 }), [checkRows]);

    const statusOptions = ['new', 'partial', 'imported', 'recheck', 'noClient', 'empty']
        .map((value) => ({ value, label: tb(`statuses.${value}`) }));

    const checkRowsPerPage = 50;
    const pagedCheckRows = checkRows.slice(checkPage * checkRowsPerPage, (checkPage + 1) * checkRowsPerPage);

    const selectMissingOnly = () => setSelected(businesses
        .filter((b) => ['new', 'partial'].includes(statusOf(b)))
        .map((b) => b.businessNumber));

    const selectSameOnly = () => setSelected(businesses
        .filter((b) => ['new', 'partial'].includes(statusOf(b)) && plStatusOf(b.profitAndLoss) === 'same')
        .map((b) => b.businessNumber));

    const selectFilteredOnly = () => setSelected(checkSelectable.map((b) => b.businessNumber));

    const toggleCheckPl = (key) => {
        setCheckPl((current) => (current === key ? null : key));
        setCheckPage(0);
    };

    const businessNames = useMemo(() => new Map(businesses.map((b) => [b.businessNumber, b.name || String(b.businessNumber)])), [businesses]);

    const vatTypeLabel = (value) => vatTypeOptions.find((o) => o.value === String(value))?.label ?? value;
    const variantText = (variant) => tb('variantLine', {
        kind: variant.isIncome ? tb('incomeSection') : tb('expenseSection'),
        vatType: vatTypeLabel(variant.sortCodeVatType),
        vat: Number(Number(variant.vat).toFixed(2)),
        pl: variant.participatesInProfitAndLoss ? t('Yes') : t('No'),
        count: variant.businessNumbers.length,
    });

    const shownMissing = onlyDifferent
        ? relevantMissing.filter(({ sc }) => (sc.variants?.length ?? 0) > 1)
        : relevantMissing;
    const differentCount = relevantMissing.filter(({ sc }) => (sc.variants?.length ?? 0) > 1).length;

    const entries = useMemo(() => (detail ? detail.entries : preview?.entries ?? []), [detail, preview]);
    const filteredEntries = useMemo(() => {
        const term = entrySearch.trim().toLowerCase();
        if (!term) return entries;
        return entries.filter((e) => [e.sortCodeName, e.reference, e.description, e.parsedEntityName, e.vatBusinessNumber, e.amount]
            .some((v) => String(v ?? '').toLowerCase().includes(term)));
    }, [entries, entrySearch]);
    const pagedEntries = filteredEntries.slice(entryPage * entryRowsPerPage, (entryPage + 1) * entryRowsPerPage);

    const duplicateCount = single
        ? (preview?.duplicateCount ?? 0)
        : businesses.filter((b) => selected.includes(b.businessNumber)).reduce((sum, b) => sum + (b.duplicateCount || 0), 0);
    const matchedCount = businesses.filter((b) => assignments[b.businessNumber]).length;

    /* ───────── Renderers ───────── */

    const renderMatch = (business) => (
        <Tooltip title={business.matchType === 'ambiguous'
            ? (business.candidates || []).map(clientLabel).join(', ')
            : ''}>
            <span>
                <XtramBadge tone={MATCH_TONES[business.matchType]}>{tb(`matchTypes.${business.matchType}`)}</XtramBadge>
            </span>
        </Tooltip>
    );

    const renderDuplicates = () => (duplicateCount > 0 ? (
        <XtramNotice type="warning">
            <span>{tb('duplicatesWarning', { count: duplicateCount })}</span>
            <XtramCheckbox checked={importDuplicates} onChange={setImportDuplicates} label={tb('importDuplicates')} />
        </XtramNotice>
    ) : null);

    const renderFile = () => (
        <>
            {caseId && (
                <div className={styles.section}>
                    <span className={styles.fieldLabel}>{tb('scopeLabel')}</span>
                    <div className={styles.toolbarGroup}>
                        <XtramButton variant="ghost" active={scope === 'client'} disabled={loading} onClick={() => changeScope('client')}>
                            {tb('scopeClient', { client: clientName || caseId })}
                        </XtramButton>
                        <XtramButton variant="ghost" active={scope === 'all'} disabled={loading} onClick={() => changeScope('all')}>
                            {tb('scopeAll')}
                        </XtramButton>
                    </div>
                </div>
            )}
            <div className={styles.section}>
                <p className={styles.muted}>{single ? tb('help') : tb('helpAll')}</p>
                <DropzoneArea onDrop={handleFileDrop} accept={ZIP_ACCEPT} />
                {file && <p className={styles.fileName}>{t('FileSelected')}: {file.name}</p>}
            </div>
            <div className={styles.section}>
                <div className={styles.formFields}>
                    <XtramField label={tb('password')} hint={tb('passwordHelp')} size="wide">
                        <XtramInput
                            direction={direction}
                            type="password"
                            autoComplete="off"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                        />
                    </XtramField>
                    {yearOptions.length > 1 && (
                        <XtramField label={t('Year')} size="narrow">
                            <XtramSelect
                                direction={direction}
                                options={yearOptions}
                                value={yearOptions.find((o) => o.value === year) ?? null}
                                onChange={(o) => o && runPreview({ nextYear: o.value })}
                                disabled={loading}
                            />
                        </XtramField>
                    )}
                </div>
            </div>
            {error && <XtramNotice type="error">{error}</XtramNotice>}
        </>
    );

    const renderMissing = () => {
        if (relevantMissing.length === 0) {
            return <XtramNotice type="success">{t('NoMissingSortCodes')}</XtramNotice>;
        }
        const bulkField = (field, options, placeholder) => (
            <XtramField size={field === 'vat' ? 'narrow' : undefined}>
                <XtramSelect
                    direction={direction}
                    options={options}
                    placeholder={placeholder}
                    value={field in bulk ? options.find((o) => o.value === bulk[field]) ?? null : null}
                    onChange={(v) => setBulk((prev) => ({ ...prev, [field]: v?.value ?? null }))}
                />
            </XtramField>
        );
        const rowField = (sc, index, field, options, placeholder, required = true) => (
            <div className={styles.cellField}>
                <XtramSelect
                    direction={direction}
                    options={options}
                    placeholder={placeholder}
                    value={options.find((o) => o.value === sc[field]) ?? null}
                    onChange={(v) => updateMissing(index, field, v?.value ?? null)}
                    error={required && sc[field] == null}
                />
            </div>
        );

        return (
            <div className={styles.section}>
                <p className={styles.muted}>{tb('missingHelp')}</p>
                {!single && (
                    <XtramNotice type={differentCount > 0 ? 'warning' : 'info'}>
                        <span>{tb('sharedHelp')}</span>
                        {differentCount > 0 && (
                            <>
                                <strong>{tb('differentCount', { count: differentCount })}</strong>
                                <XtramCheckbox checked={onlyDifferent} onChange={setOnlyDifferent} label={tb('onlyDifferent')} />
                            </>
                        )}
                    </XtramNotice>
                )}
                <div className={styles.formFields}>
                    {bulkField('sortCodeVatType', vatTypeOptions, t('SelectSortCodeVatType'))}
                    {bulkField('sortCodeCategory', groupOptions, t('SelectSortCodeGroupName'))}
                    {bulkField('participatesInProfitAndLoss', yesNoOptions, t('participatesInProfitAndLoss'))}
                    {bulkField('vat', vatOptions, t('VAT'))}
                    <XtramButton
                        variant="ghost"
                        disabled={selectedMissing.length === 0 || Object.keys(bulk).length === 0}
                        onClick={applyBulk}
                    >
                        {t('ApplyToSelected')}
                    </XtramButton>
                </div>
                <XtramTable height="tall">
                    <Table size="small" stickyHeader dir={direction}>
                        <TableHead>
                            <TableRow>
                                <TableCell padding="checkbox" className={styles.actionsCell}>
                                    <Checkbox
                                        size="small"
                                        className={styles.tableCheckbox}
                                        checked={relevantMissing.every(({ sc }) => selectedMissing.includes(sc.sortCodeId))}
                                        onChange={(e) => setSelectedMissing(e.target.checked ? relevantMissing.map(({ sc }) => sc.sortCodeId) : [])}
                                    />
                                </TableCell>
                                <TableCell>{t('SortCode Id')}</TableCell>
                                <TableCell>{tb('sectionNumbers')}</TableCell>
                                <TableCell>{t('SortCode Name')}</TableCell>
                                <TableCell>{t('SelectSortCodeVatType')}</TableCell>
                                <TableCell>{t('SelectSortCodeGroupName')}</TableCell>
                                <TableCell>{t('participatesInProfitAndLoss')}</TableCell>
                                <TableCell>{t('VAT')}</TableCell>
                                {!single && <TableCell>{tb('usedBy')}</TableCell>}
                                {!single && <TableCell>{tb('settingsInBusinesses')}</TableCell>}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {shownMissing.map(({ sc, index }) => (
                                <TableRow key={sc.sortCodeId}>
                                    <TableCell padding="checkbox" className={styles.actionsCell}>
                                        <Checkbox
                                            size="small"
                                            className={styles.tableCheckbox}
                                            checked={selectedMissing.includes(sc.sortCodeId)}
                                            onChange={(e) => setSelectedMissing((prev) => (e.target.checked
                                                ? [...prev, sc.sortCodeId]
                                                : prev.filter((id) => id !== sc.sortCodeId)))}
                                        />
                                    </TableCell>
                                    <TableCell>{sc.sortCodeId}</TableCell>
                                    <TableCell>{(sc.sectionNumbers || []).join(', ') || '—'}</TableCell>
                                    <TableCell>
                                        <div className={styles.cellFieldWide}>
                                            <XtramInput
                                                direction={direction}
                                                value={sc.sortCodeName}
                                                onChange={(e) => updateMissing(index, 'sortCodeName', e.target.value)}
                                                error={!sc.sortCodeName?.trim()}
                                            />
                                        </div>
                                    </TableCell>
                                    <TableCell>{rowField(sc, index, 'sortCodeVatType', vatTypeOptions, t('SelectSortCodeVatType'))}</TableCell>
                                    <TableCell>{rowField(sc, index, 'sortCodeCategory', groupOptions, t('SelectSortCodeGroupName'), false)}</TableCell>
                                    <TableCell>{rowField(sc, index, 'participatesInProfitAndLoss', yesNoOptions, t('participatesInProfitAndLoss'))}</TableCell>
                                    <TableCell>{rowField(sc, index, 'vat', vatOptions, t('VAT'))}</TableCell>
                                    {!single && (
                                        <TableCell>
                                            <Tooltip title={<span style={{ whiteSpace: 'pre-line' }}>
                                                {sc.businessNumbers.map((n) => `${n} · ${businessNames.get(n) ?? ''}`).join('\n')}
                                            </span>}>
                                                <span>{tb('businessCount', { count: sc.businessNumbers.length })}</span>
                                            </Tooltip>
                                        </TableCell>
                                    )}
                                    {!single && (
                                        <TableCell>
                                            {(sc.variants?.length ?? 0) > 1 ? (
                                                <XtramButton
                                                    variant="ghost"
                                                    small
                                                    onClick={(e) => setVariantMenu({ anchor: e.currentTarget, index, sc })}
                                                >
                                                    <XtramBadge tone="warning">{tb('differentSettings', { count: sc.variants.length })}</XtramBadge>
                                                </XtramButton>
                                            ) : (
                                                <XtramBadge tone="success">{tb('sameSettings')}</XtramBadge>
                                            )}
                                        </TableCell>
                                    )}
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </XtramTable>
                <Menu
                    anchorEl={variantMenu?.anchor ?? null}
                    open={!!variantMenu}
                    onClose={() => setVariantMenu(null)}
                    dir={direction}
                >
                    <MenuItem disabled>{tb('chooseVariant')}</MenuItem>
                    {(variantMenu?.sc.variants || []).map((variant, i) => {
                        const current = missing[variantMenu.index];
                        return (
                            <MenuItem
                                key={i}
                                selected={!!current && isVariantApplied(current, variant)}
                                onClick={() => applyVariant(variantMenu.index, variant)}
                            >
                                <Tooltip
                                    placement={direction === 'rtl' ? 'left' : 'right'}
                                    title={<span style={{ whiteSpace: 'pre-line' }}>
                                        {variant.businessNumbers.map((n) => `${n} · ${businessNames.get(n) ?? ''}`).join('\n')}
                                    </span>}
                                >
                                    <span>{variantText(variant)}</span>
                                </Tooltip>
                            </MenuItem>
                        );
                    })}
                </Menu>
            </div>
        );
    };

    const currentSimulation = simulationKey(missing, importDuplicates);

    const renderStaleSimulation = (stale, onRefresh) => stale && (
        <XtramNotice type="warning">
            <span>{tb('plStale')}</span>
            <div>
                <XtramButton variant="secondary" small disabled={loading} onClick={onRefresh}>{tb('plRefresh')}</XtramButton>
            </div>
        </XtramNotice>
    );

    const renderProfitAndLoss = (pl, { stale = false, onRefresh } = {}) => {
        if (!pl) return <XtramNotice type="warning">{tb('plUnavailable')}</XtramNotice>;
        const status = plStatusOf(pl);
        const lines = plOnlyDifferent ? pl.lines.filter((l) => !l.isSame) : pl.lines;
        return (
            <div className={styles.section}>
                <div className={styles.toolbar}>
                    <h3 className={styles.sectionTitle}>{tb('plTitle', { year: pl.year })}</h3>
                    <div className={styles.toolbarGroup}>
                        <XtramCheckbox checked={plOnlyDifferent} onChange={setPlOnlyDifferent} label={tb('plOnlyDifferentLines')} />
                        <XtramButton variant="ghost" small disabled={loading} onClick={onRefresh}>{tb('plRefresh')}</XtramButton>
                    </div>
                </div>
                <p className={styles.muted}>{tb('plHelp')}</p>
                {renderStaleSimulation(stale, onRefresh)}
                <XtramNotice type="info">
                    {pl.importedEntryCount > 0
                        ? tb('plSimulation', { count: pl.importedEntryCount })
                        : tb('plNothingToImport')}
                </XtramNotice>
                {status === 'same' && <XtramNotice type="success">{tb('plSame')}</XtramNotice>}
                {status === 'noData' && <XtramNotice type="info">{tb('plNoData')}</XtramNotice>}
                {status === 'different' && (
                    <XtramNotice type="warning">{tb('plDifferent', { count: pl.differentLineCount })}</XtramNotice>
                )}
                {pl.outsideYearRowCount > 0 && (
                    <XtramNotice type="info">{tb('plOutsideYear', { count: pl.outsideYearRowCount })}</XtramNotice>
                )}
                <div className={styles.stats}>
                    <XtramStat label={tb('atzmaitIncome')} value={formatAmount(pl.atzmaitIncome)} />
                    <XtramStat label={tb('systemIncome')} value={formatAmount(pl.systemIncome)} />
                    <XtramStat label={tb('atzmaitExpenses')} value={formatAmount(pl.atzmaitExpenses)} />
                    <XtramStat label={tb('systemExpenses')} value={formatAmount(pl.systemExpenses)} />
                    <XtramStat label={tb('atzmaitProfit')} value={formatAmount(pl.atzmaitProfit)} />
                    <XtramStat label={tb('systemProfit')} value={formatAmount(pl.systemProfit)} />
                    <XtramStat label={tb('currentProfit')} value={formatAmount(pl.currentProfit)} />
                    <XtramStat
                        label={tb('plDifference')}
                        value={formatAmount(pl.difference)}
                        tone={status === 'same' ? 'success' : 'warning'}
                    />
                </div>
                <XtramTable height="medium">
                    <Table size="small" stickyHeader dir={direction}>
                        <TableHead>
                            <TableRow>
                                <TableCell>{tb('section')}</TableCell>
                                <TableCell>{t('SortCode Id')}</TableCell>
                                <TableCell>{t('SortCode Name')}</TableCell>
                                <TableCell>{tb('systemGroup')}</TableCell>
                                <TableCell className={styles.numCell}>{tb('atzmaitAmount')}</TableCell>
                                <TableCell className={styles.numCell}>{tb('currentAmount')}</TableCell>
                                <TableCell className={styles.numCell}>{tb('systemAmount')}</TableCell>
                                <TableCell className={styles.numCell}>{tb('plDifference')}</TableCell>
                                <TableCell>{tb('status')}</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {lines.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={9} className={styles.emptyState}>{t('importModal.noResults')}</TableCell>
                                </TableRow>
                            ) : lines.map((line) => (
                                <TableRow
                                    key={line.sortCodeId ?? `name:${line.name}`}
                                    className={line.isSame ? undefined : styles.rowWarning}
                                >
                                    <TableCell>{line.sectionNumbers?.length ? line.sectionNumbers.join(', ') : '—'}</TableCell>
                                    <TableCell>
                                        {line.sortCodeId ?? <span className={styles.textError}>{tb('plNoSortCode')}</span>}
                                        {line.isNewSortCode && (
                                            <Tooltip title={tb('plNewSortCodeHelp')}>
                                                <span> <XtramBadge tone="info">{tb('plNewSortCode')}</XtramBadge></span>
                                            </Tooltip>
                                        )}
                                    </TableCell>
                                    <TableCell className={styles.wrapCell}>
                                        {line.name}
                                        {line.inAtzmaitProfitAndLoss === false && (
                                            <div className={styles.muted}>{tb('plNotInAtzmait')}</div>
                                        )}
                                    </TableCell>
                                    <TableCell className={styles.wrapCell}>
                                        {line.systemGroup || '—'}
                                        {line.isVirtualGroup && <div className={styles.muted}>{tb('plVirtualGroup')}</div>}
                                    </TableCell>
                                    <TableCell className={styles.numCell}>{formatAmount(line.atzmaitAmount)}</TableCell>
                                    <TableCell className={styles.numCell}>{formatAmount(line.currentAmount)}</TableCell>
                                    <TableCell className={styles.numCell}>{formatAmount(line.systemAmount)}</TableCell>
                                    <TableCell className={`${styles.numCell} ${line.isSame ? '' : styles.textError}`}>
                                        {formatAmount(line.difference)}
                                    </TableCell>
                                    <TableCell>
                                        <XtramBadge tone={line.isSame ? 'success' : 'error'}>
                                            {tb(`plStatuses.${line.isSame ? 'same' : 'different'}`)}
                                        </XtramBadge>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </XtramTable>
            </div>
        );
    };

    const renderCheck = () => {
        if (detail) {
            return (
                <div className={styles.section}>
                    <div className={styles.toolbar}>
                        <div className={styles.toolbarGroup}>
                            <XtramButton variant="secondary" small onClick={() => setDetail(null)}>{tb('back')}</XtramButton>
                            <h3 className={styles.sectionTitle}>
                                {businessLabel(detail.business)} ← {clientLabel(clientsByCase.get(detail.caseId))}
                            </h3>
                        </div>
                    </div>
                    {detail.loading
                        ? <div className={styles.loading}><CircularProgress className={styles.spinner} /></div>
                        : (
                            <>
                                {renderProfitAndLoss(detail.profitAndLoss, {
                                    stale: detail.simulatedWith !== currentSimulation,
                                    onRefresh: () => openDetail(detail.business),
                                })}
                                {renderEntries()}
                            </>
                        )}
                </div>
            );
        }

        return (
            <div className={styles.section}>
                {checkingCount > 0 && (
                    <XtramNotice type="info">
                        <span className={styles.toolbarGroup}>
                            <CircularProgress size={16} />
                            {tb('checkingNotice', { count: checkingCount })}
                        </span>
                    </XtramNotice>
                )}
                {checkingCount === 0 && plSummary.unchecked > 0 && (
                    <XtramNotice type="warning">
                        <span>{tb('recheckNotice', { count: plSummary.unchecked })}</span>
                        <div>
                            <XtramButton variant="secondary" small disabled={loading} onClick={() => runPreview()}>
                                {tb('recheckAll')}
                            </XtramButton>
                        </div>
                    </XtramNotice>
                )}
                {renderStaleSimulation(simulatedWith !== currentSimulation, () => runPreview())}
                <h3 className={styles.sectionTitle}>{tb('plSummaryTitle')}</h3>
                <p className={styles.muted}>{tb('plSummaryHelp')}</p>
                <div className={styles.stats}>
                    {PL_FILTERS.map((key) => (
                        <XtramStat
                            key={key}
                            label={tb(`plSummary.${key}`)}
                            value={plSummary[key]}
                            tone={plSummary[key] > 0 ? PL_TONES[key] : undefined}
                            active={checkPl === key}
                            onClick={() => toggleCheckPl(key)}
                        />
                    ))}
                </div>
                <p className={styles.muted}>{tb('checkHelp')}</p>
                <div className={styles.stats}>
                    <XtramStat label={tb('rows')} value={checkTotals.rows} />
                    <XtramStat label={tb('inSystem')} value={checkTotals.inSystem} tone={checkTotals.inSystem ? 'warning' : undefined} />
                    <XtramStat label={tb('newRows')} value={checkTotals.newRows} tone="success" />
                    <XtramStat label={tb('backupAmount')} value={formatAmount(checkTotals.backupAmount)} />
                    <XtramStat label={tb('inSystemAmount')} value={formatAmount(checkTotals.inSystemAmount)} />
                    <XtramStat label={tb('newAmount')} value={formatAmount(checkTotals.newAmount)} />
                </div>
                <div className={styles.toolbar}>
                    <div className={styles.toolbarGroup}>
                        <div className={styles.formFieldWide}>
                            <XtramSearch
                                direction={direction}
                                value={checkSearch}
                                onChange={(v) => { setCheckSearch(v); setCheckPage(0); }}
                                placeholder={t('Search')}
                            />
                        </div>
                        <div className={styles.formField}>
                            <XtramSelect
                                direction={direction}
                                options={statusOptions}
                                value={checkStatus}
                                onChange={(v) => { setCheckStatus(v); setCheckPage(0); }}
                                placeholder={tb('allStatuses')}
                            />
                        </div>
                        {checkPl && (
                            <XtramButton variant="ghost" small onClick={() => toggleCheckPl(checkPl)}>
                                {tb('clearPlFilter', { status: tb(`plSummary.${checkPl}`) })}
                            </XtramButton>
                        )}
                    </div>
                    <div className={styles.toolbarGroup}>
                        <XtramButton variant="ghost" small onClick={selectSameOnly}>{tb('selectSameOnly')}</XtramButton>
                        <XtramButton variant="ghost" small onClick={selectMissingOnly}>{tb('selectMissingOnly')}</XtramButton>
                        <XtramButton variant="ghost" small disabled={checkSelectable.length === 0} onClick={selectFilteredOnly}>
                            {tb('selectFilteredOnly', { count: checkSelectable.length })}
                        </XtramButton>
                        <XtramButton variant="ghost" small disabled={selected.length === 0} onClick={() => setSelected([])}>
                            {tb('clearSelection')}
                        </XtramButton>
                    </div>
                </div>
                <XtramNotice type={selectedSummary.different || selectedSummary.unchecked ? 'warning' : 'success'}>
                    <span>
                        {tb('importBar', { count: selectedSummary.count })}
                        {selectedSummary.different > 0 && ` · ${tb('importBarDifferent', { count: selectedSummary.different })}`}
                        {selectedSummary.unchecked > 0 && ` · ${tb('importBarUnchecked', { count: selectedSummary.unchecked })}`}
                    </span>
                    <div>
                        <XtramButton variant="primary" small disabled={!canCommit} busy={loading} onClick={handleCommit}>
                            {tb('importSelected', { count: commitAssignments.length })}
                        </XtramButton>
                    </div>
                </XtramNotice>
                <XtramTable height="tall">
                    <Table size="small" stickyHeader dir={direction}>
                        <TableHead>
                            <TableRow>
                                <TableCell padding="checkbox" className={styles.actionsCell}>
                                    <Checkbox
                                        size="small"
                                        className={styles.tableCheckbox}
                                        checked={checkSelectable.length > 0
                                            && checkSelectable.every((b) => selected.includes(b.businessNumber))}
                                        disabled={checkSelectable.length === 0}
                                        onChange={(e) => {
                                            const numbers = checkSelectable.map((b) => b.businessNumber);
                                            setSelected((prev) => (e.target.checked
                                                ? [...new Set([...prev, ...numbers])]
                                                : prev.filter((n) => !numbers.includes(n))));
                                        }}
                                    />
                                </TableCell>
                                <TableCell>{tb('businessNumber')}</TableCell>
                                <TableCell>{t('BusinessName')}</TableCell>
                                <TableCell>{tb('client')}</TableCell>
                                <TableCell>{tb('rows')}</TableCell>
                                <TableCell>{tb('inSystem')}</TableCell>
                                <TableCell>{tb('newRows')}</TableCell>
                                <TableCell className={styles.numCell}>{tb('backupAmount')}</TableCell>
                                <TableCell className={styles.numCell}>{tb('inSystemAmount')}</TableCell>
                                <TableCell className={styles.numCell}>{tb('newAmount')}</TableCell>
                                <TableCell>{tb('status')}</TableCell>
                                <TableCell className={styles.numCell}>{tb('atzmaitProfit')}</TableCell>
                                <TableCell className={styles.numCell}>{tb('systemProfit')}</TableCell>
                                <TableCell className={styles.numCell}>{tb('plDifference')}</TableCell>
                                <TableCell>{tb('plColumn')}</TableCell>
                                <TableCell className={styles.actionsCell} />
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {pagedCheckRows.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={16} className={styles.emptyState}>{t('importModal.noResults')}</TableCell>
                                </TableRow>
                            )}
                            {pagedCheckRows.map((row) => {
                                const b = row.business;
                                const counted = ['new', 'partial', 'imported'].includes(row.status);
                                return (
                                    <TableRow
                                        key={b.businessNumber}
                                        hover
                                        className={row.plKey === 'different' ? styles.rowWarning : undefined}
                                    >
                                        <TableCell padding="checkbox" className={styles.actionsCell}>
                                            <Checkbox
                                                size="small"
                                                className={styles.tableCheckbox}
                                                disabled={!canImport(b)}
                                                checked={canImport(b) && selected.includes(b.businessNumber)}
                                                onChange={(e) => setSelected((prev) => (e.target.checked
                                                    ? [...prev, b.businessNumber]
                                                    : prev.filter((n) => n !== b.businessNumber)))}
                                            />
                                        </TableCell>
                                        <TableCell>{b.businessNumber}</TableCell>
                                        <TableCell className={styles.wrapCell}>{b.name || '—'}</TableCell>
                                        <TableCell className={styles.wrapCell}>{row.client ? clientLabel(row.client) : '—'}</TableCell>
                                        <TableCell>{b.rowCount}</TableCell>
                                        <TableCell className={counted && b.duplicateCount ? styles.textWarning : undefined}>
                                            {counted ? b.duplicateCount || 0 : '—'}
                                        </TableCell>
                                        <TableCell className={counted && row.newCount ? styles.textSuccess : undefined}>
                                            {counted ? row.newCount : '—'}
                                        </TableCell>
                                        <TableCell className={styles.numCell}>{formatAmount(row.backupAmount)}</TableCell>
                                        <TableCell className={styles.numCell}>{counted ? formatAmount(row.inSystemAmount) : '—'}</TableCell>
                                        <TableCell className={styles.numCell}>{counted ? formatAmount(row.newAmount) : '—'}</TableCell>
                                        <TableCell>
                                            <Tooltip title={row.status === 'recheck' ? tb('recheckHelp') : ''}>
                                                <span>
                                                    <XtramBadge tone={STATUS_TONES[row.status]}>{tb(`statuses.${row.status}`)}</XtramBadge>
                                                </span>
                                            </Tooltip>
                                        </TableCell>
                                        <TableCell className={styles.numCell}>
                                            {row.plStatus ? formatAmount(b.profitAndLoss.atzmaitProfit) : '—'}
                                        </TableCell>
                                        <TableCell className={`${styles.numCell} ${row.plStatus === 'different' ? styles.textError : ''}`}>
                                            {row.plStatus ? formatAmount(b.profitAndLoss.systemProfit) : '—'}
                                        </TableCell>
                                        <TableCell className={`${styles.numCell} ${row.plStatus === 'different' ? styles.textError : ''}`}>
                                            {row.plStatus ? formatAmount(b.profitAndLoss.difference) : '—'}
                                        </TableCell>
                                        <TableCell>{renderPlBadge(b, row.plKey)}</TableCell>
                                        <TableCell className={styles.actionsCell}>
                                            <XtramButton
                                                variant="ghost"
                                                small
                                                disabled={!row.client || b.rowCount === 0}
                                                onClick={() => openDetail(b)}
                                            >
                                                {tb('showEntries')}
                                            </XtramButton>
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                    <TablePagination
                        component="div"
                        count={checkRows.length}
                        page={checkPage}
                        onPageChange={(_, p) => setCheckPage(p)}
                        rowsPerPage={checkRowsPerPage}
                        rowsPerPageOptions={[checkRowsPerPage]}
                        labelRowsPerPage={t('RowsPerPage')}
                    />
                </XtramTable>
            </div>
        );
    };

    const renderEntries = () => (
        <div className={styles.section}>
            <div className={styles.toolbar}>
                <h3 className={styles.sectionTitle}>
                    {tb('entries')} ({filteredEntries.length}{entrySearch ? ` / ${entries.length}` : ''})
                </h3>
                <div className={styles.formField}>
                    <XtramSearch
                        direction={direction}
                        value={entrySearch}
                        onChange={(v) => { setEntrySearch(v); setEntryPage(0); }}
                        placeholder={t('Search')}
                    />
                </div>
            </div>
            <XtramTable height="tall">
                <Table size="small" stickyHeader dir={direction}>
                    <TableHead>
                        <TableRow>
                            <TableCell>{t('Line')}</TableCell>
                            <TableCell>{t('DocumentDate')}</TableCell>
                            <TableCell>{t('ReportingMonth')}</TableCell>
                            <TableCell>{t('SortCode Id')}</TableCell>
                            <TableCell>{t('SortCode Name')}</TableCell>
                            <TableCell className={styles.numCell}>{t('Amount')}</TableCell>
                            <TableCell className={styles.numCell}>{t('SumNetAmount')}</TableCell>
                            <TableCell className={styles.numCell}>{t('VAT')}</TableCell>
                            <TableCell className={styles.numCell}>{t('WithholdingTax')}</TableCell>
                            <TableCell>{t('WithholdingParty')}</TableCell>
                            <TableCell>{t('Reference')}</TableCell>
                            <TableCell>{t('BusinessName')}</TableCell>
                            <TableCell>{tb('dealerNumber')}</TableCell>
                            <TableCell>{t('Description')}</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {pagedEntries.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={14} className={styles.emptyState}>{t('importModal.noResults')}</TableCell>
                            </TableRow>
                        ) : pagedEntries.map((row, i) => (
                            <TableRow key={`${row.lineNumber}-${i}`} className={row.isDuplicate ? styles.rowWarning : undefined}>
                                <TableCell>
                                    {row.lineNumber}
                                    {row.isDuplicate && (
                                        <Tooltip title={t('DuplicateInDb')}>
                                            <span className={styles.duplicateMark}>⚠</span>
                                        </Tooltip>
                                    )}
                                </TableCell>
                                <TableCell>{formatDate(row.documentDate)}</TableCell>
                                <TableCell>{String(row.reportingMonthDate || '').slice(0, 7)}</TableCell>
                                <TableCell>{row.sortCodeId}</TableCell>
                                <TableCell className={styles.wrapCell}>{row.sortCodeName}</TableCell>
                                <TableCell className={styles.numCell}>{formatAmount(row.amount)}</TableCell>
                                <TableCell className={styles.numCell}>{formatAmount(row.originalAmount)}</TableCell>
                                <TableCell className={styles.numCell}>{formatAmount(row.vatAmount)}</TableCell>
                                <TableCell className={`${styles.numCell} ${Number(row.withholdingTaxAmount) ? styles.cellWarning : ''}`}>
                                    {formatAmount(row.withholdingTaxAmount)}
                                </TableCell>
                                <TableCell>
                                    {row.withholdingParty === 'supplier' ? t('Supplier') : row.withholdingParty === 'client' ? t('Client') : '—'}
                                </TableCell>
                                <TableCell>{row.reference || '—'}</TableCell>
                                <TableCell className={styles.wrapCell}>{row.parsedEntityName || '—'}</TableCell>
                                <TableCell>{row.vatBusinessNumber || '—'}</TableCell>
                                <TableCell className={styles.wrapCell}>{row.description}</TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
                <TablePagination
                    component="div"
                    count={filteredEntries.length}
                    page={entryPage}
                    onPageChange={(_, p) => setEntryPage(p)}
                    rowsPerPage={entryRowsPerPage}
                    onRowsPerPageChange={(e) => { setEntryRowsPerPage(parseInt(e.target.value, 10)); setEntryPage(0); }}
                    rowsPerPageOptions={[25, 50, 100]}
                    labelRowsPerPage={t('RowsPerPage')}
                />
            </XtramTable>
        </div>
    );

    const renderSingle = () => {
        const otherClient = selectedBusiness?.caseId && selectedBusiness.caseId !== caseId;
        return (
            <>
                <div className={styles.section}>
                    <XtramField label={tb('business')} size="full">
                        <div className={styles.formFieldWide}>
                            <XtramSelect
                                direction={direction}
                                options={businesses}
                                value={selectedBusiness}
                                onChange={(b) => b && runPreview({ businessNumber: b.businessNumber })}
                                getOptionLabel={businessLabel}
                                isOptionEqualToValue={(a, b) => a.businessNumber === b.businessNumber}
                                placeholder={tb('chooseBusiness')}
                                disabled={loading}
                            />
                        </div>
                    </XtramField>
                    {!selectedBusiness && (
                        <XtramNotice type="warning">
                            <span>{tb('noBusinessMatched')}</span>
                            <div>
                                <XtramButton variant="secondary" small disabled={loading} onClick={() => changeScope('all', true)}>
                                    {tb('loadAllClients')}
                                </XtramButton>
                            </div>
                        </XtramNotice>
                    )}
                    {selectedBusiness && otherClient && (
                        <XtramNotice type="warning">
                            {tb('otherClientWarning', { name: selectedBusiness.clientName || selectedBusiness.caseId })}
                        </XtramNotice>
                    )}
                    {selectedBusiness && !selectedBusiness.caseId && (
                        <XtramNotice type="info">
                            {tb('willImportTo', { dealer: selectedBusiness.dealerNumber || '—', client: clientName || caseId })}
                        </XtramNotice>
                    )}
                    {selectedBusiness && (
                        <div className={styles.stats}>
                            <XtramStat label={tb('rows')} value={selectedBusiness.rowCount} />
                            <XtramStat
                                label={tb('period')}
                                value={`${formatDate(selectedBusiness.fromDate)} – ${formatDate(selectedBusiness.toDate)}`}
                            />
                            <XtramStat label={tb('income')} value={formatAmount(selectedBusiness.incomeTotal)} />
                            <XtramStat label={tb('expenses')} value={formatAmount(selectedBusiness.expenseTotal)} />
                            {selectedBusiness.skippedRowCount > 0 && (
                                <Tooltip title={tb('skippedHelp')}>
                                    <div><XtramStat label={tb('skipped')} value={selectedBusiness.skippedRowCount} tone="warning" /></div>
                                </Tooltip>
                            )}
                            {duplicateCount > 0 && <XtramStat label={tb('duplicates')} value={duplicateCount} tone="warning" />}
                        </div>
                    )}
                    {renderDuplicates()}
                </div>
                {selectedBusiness && selectedBusiness.rowCount > 0 && renderProfitAndLoss(selectedBusiness.profitAndLoss, {
                    stale: simulatedWith !== currentSimulation,
                    onRefresh: () => runPreview({ businessNumber: selectedBusiness.businessNumber }),
                })}
                {selectedBusiness && (entries.length > 0
                    ? renderEntries()
                    : <div className={styles.emptyState}>{tb('noRows')}</div>)}
            </>
        );
    };

    const renderAll = () => (
        <div className={styles.section}>
            <div className={styles.stats}>
                <XtramStat label={t('importModal.businessesTab')} value={businesses.length} />
                <XtramStat label={tb('matched')} value={matchedCount} tone="success" />
                <XtramStat label={tb('toImport')} value={commitAssignments.length} />
                {duplicateCount > 0 && <XtramStat label={tb('duplicates')} value={duplicateCount} tone="warning" />}
                {['same', 'different'].map((key) => (
                    <XtramStat
                        key={key}
                        label={tb(`plSummary.${key}`)}
                        value={plSummary[key]}
                        tone={plSummary[key] > 0 ? PL_TONES[key] : undefined}
                        onClick={() => { setCheckPl(key); setCheckPage(0); setTab('check'); }}
                    />
                ))}
            </div>
            {checkingCount > 0 && (
                <XtramNotice type="info">
                    <span className={styles.toolbarGroup}>
                        <CircularProgress size={16} />
                        {tb('checkingNotice', { count: checkingCount })}
                    </span>
                </XtramNotice>
            )}
            {renderDuplicates()}
            <div className={styles.toolbar}>
                <div className={styles.toolbarGroup}>
                    <div className={styles.formFieldWide}>
                        <XtramSearch
                            direction={direction}
                            value={businessSearch}
                            onChange={(v) => { setBusinessSearch(v); setBusinessPage(0); }}
                            placeholder={t('Search')}
                        />
                    </div>
                    <XtramCheckbox
                        checked={hideEmpty}
                        onChange={(v) => { setHideEmpty(v); setBusinessPage(0); }}
                        label={tb('hideEmpty')}
                    />
                    <XtramCheckbox
                        checked={onlyUnmatched}
                        onChange={(v) => { setOnlyUnmatched(v); setBusinessPage(0); }}
                        label={tb('onlyUnmatched')}
                    />
                </div>
            </div>
            <XtramTable height="tall">
                <Table size="small" stickyHeader dir={direction}>
                    <TableHead>
                        <TableRow>
                            <TableCell padding="checkbox" className={styles.actionsCell}>
                                <Checkbox
                                    size="small"
                                    className={styles.tableCheckbox}
                                    checked={allVisibleSelected}
                                    disabled={selectableVisible.length === 0}
                                    onChange={(e) => {
                                        const numbers = selectableVisible.map((b) => b.businessNumber);
                                        setSelected((prev) => (e.target.checked
                                            ? [...new Set([...prev, ...numbers])]
                                            : prev.filter((n) => !numbers.includes(n))));
                                    }}
                                />
                            </TableCell>
                            <TableCell>{tb('businessNumber')}</TableCell>
                            <TableCell>{t('BusinessName')}</TableCell>
                            <TableCell>{tb('dealerNumber')}</TableCell>
                            <TableCell>{tb('rows')}</TableCell>
                            <TableCell>{tb('period')}</TableCell>
                            <TableCell className={styles.numCell}>{tb('income')}</TableCell>
                            <TableCell className={styles.numCell}>{tb('expenses')}</TableCell>
                            <TableCell>{tb('client')}</TableCell>
                            <TableCell>{tb('match')}</TableCell>
                            <TableCell>{tb('duplicates')}</TableCell>
                            <TableCell>{tb('plColumn')}</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {pagedBusinesses.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={12} className={styles.emptyState}>{t('importModal.noResults')}</TableCell>
                            </TableRow>
                        )}
                        {pagedBusinesses.map((b) => {
                            const assigned = clientsByCase.get(assignments[b.businessNumber]) ?? null;
                            const canSelect = !!assigned && b.rowCount > 0;
                            return (
                                <TableRow key={b.businessNumber} hover>
                                    <TableCell padding="checkbox" className={styles.actionsCell}>
                                        <Checkbox
                                            size="small"
                                            className={styles.tableCheckbox}
                                            disabled={!canSelect}
                                            checked={canSelect && selected.includes(b.businessNumber)}
                                            onChange={(e) => setSelected((prev) => (e.target.checked
                                                ? [...prev, b.businessNumber]
                                                : prev.filter((n) => n !== b.businessNumber)))}
                                        />
                                    </TableCell>
                                    <TableCell>{b.businessNumber}</TableCell>
                                    <TableCell className={styles.wrapCell}>{b.name || '—'}</TableCell>
                                    <TableCell>{b.dealerNumber || '—'}</TableCell>
                                    <TableCell>
                                        {b.rowCount}
                                        {b.skippedRowCount > 0 && (
                                            <Tooltip title={tb('skippedHelp')}>
                                                <span className={styles.fieldHint}> (+{b.skippedRowCount})</span>
                                            </Tooltip>
                                        )}
                                    </TableCell>
                                    <TableCell>{b.fromDate ? `${formatDate(b.fromDate)} – ${formatDate(b.toDate)}` : '—'}</TableCell>
                                    <TableCell className={styles.numCell}>{formatAmount(b.incomeTotal)}</TableCell>
                                    <TableCell className={styles.numCell}>{formatAmount(b.expenseTotal)}</TableCell>
                                    <TableCell>
                                        <div className={styles.cellFieldWide}>
                                            <XtramSelect
                                                direction={direction}
                                                options={clients}
                                                value={assigned}
                                                onChange={(client) => changeClient(b, client)}
                                                getOptionLabel={clientLabel}
                                                isOptionEqualToValue={(x, y) => x.caseId === y.caseId}
                                                filterOptions={filterClients}
                                                placeholder={tb('chooseClient')}
                                            />
                                        </div>
                                    </TableCell>
                                    <TableCell>{renderMatch(b)}</TableCell>
                                    <TableCell className={b.duplicateCount > 0 ? styles.textWarning : undefined}>
                                        {b.duplicateCount || 0}
                                    </TableCell>
                                    <TableCell>
                                        {b.rowCount > 0 && assigned ? (
                                            <Tooltip title={statusOf(b) === 'recheck' || !b.profitAndLoss
                                                ? ''
                                                : tb('plTooltip', {
                                                    atzmait: formatAmount(b.profitAndLoss.atzmaitProfit),
                                                    system: formatAmount(b.profitAndLoss.systemProfit),
                                                    difference: formatAmount(b.profitAndLoss.difference),
                                                })}>
                                                <span>{renderPlBadge(b, plKeyOf(b, statusOf(b)))}</span>
                                            </Tooltip>
                                        ) : '—'}
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
                <TablePagination
                    component="div"
                    count={visibleBusinesses.length}
                    page={businessPage}
                    onPageChange={(_, p) => setBusinessPage(p)}
                    rowsPerPage={businessRowsPerPage}
                    rowsPerPageOptions={[businessRowsPerPage]}
                    labelRowsPerPage={t('RowsPerPage')}
                />
            </XtramTable>
        </div>
    );

    const renderResults = () => (
        <div className={styles.section}>
            <div className={styles.stats}>
                <XtramStat label={tb('importedCount')} value={results?.processedCount ?? 0} tone="success" />
                <XtramStat
                    label={tb('errorCount')}
                    value={results?.errorCount ?? 0}
                    tone={results?.errorCount > 0 ? 'warning' : undefined}
                />
            </div>
            <XtramTable height="tall">
                <Table size="small" stickyHeader dir={direction}>
                    <TableHead>
                        <TableRow>
                            <TableCell>{tb('businessNumber')}</TableCell>
                            <TableCell>{t('BusinessName')}</TableCell>
                            <TableCell>{tb('client')}</TableCell>
                            <TableCell>{tb('importedCount')}</TableCell>
                            <TableCell>{tb('errorCount')}</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {(results?.results || []).map((r) => (
                            <TableRow key={`${r.businessNumber}-${r.caseId}`}>
                                <TableCell>{r.businessNumber}</TableCell>
                                <TableCell>{r.businessName || '—'}</TableCell>
                                <TableCell>{clientLabel({ name: r.clientName, caseId: r.caseId })}</TableCell>
                                <TableCell>{r.processedCount}</TableCell>
                                <TableCell>
                                    {r.errorCount > 0 ? (
                                        <Tooltip title={<span style={{ whiteSpace: 'pre-line' }}>{(r.errorMessages || []).slice(0, 20).join('\n')}</span>}>
                                            <span className={styles.textWarning} style={{ cursor: 'help' }}>{r.errorCount}</span>
                                        </Tooltip>
                                    ) : 0}
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </XtramTable>
        </div>
    );

    const tabs = [
        { key: 'file', label: t('importModal.fileTab') },
        {
            key: 'data',
            label: single ? tb('entries') : t('importModal.businessesTab'),
            count: preview ? (single ? entries.length : businesses.length) : null,
            disabled: !preview,
        },
        ...(single ? [] : [{
            key: 'check',
            label: tb('checkTab'),
            count: preview ? pendingCount : null,
            disabled: !preview,
        }]),
        {
            key: 'missing',
            label: t('MissingSortCodes'),
            count: preview ? relevantMissing.length : null,
            alert: missingIncomplete,
            disabled: !preview,
        },
        ...(results ? [{ key: 'results', label: tb('results') }] : []),
    ];

    const canCommit = !!preview && !loading && commitAssignments.length > 0;

    return (
        <XtramModal
            open={!!open}
            onClose={handleClose}
            direction={direction}
            busy={loading}
            expanded={!!preview || !!results}
            title={single ? tb('title', { client: clientName || caseId }) : tb('titleAll')}
            tabs={tabs}
            activeTab={tab}
            onTabChange={setTab}
            footer={(
                <>
                    {preview && !single && (
                        <span className={styles.footerInfo}>{tb('selectedCount', { count: commitAssignments.length })}</span>
                    )}
                    <XtramButton variant="secondary" onClick={handleClose} disabled={loading}>
                        {t('cancelButton')}
                    </XtramButton>
                    <XtramButton variant="ghost" onClick={() => runPreview()} disabled={!file || loading}>
                        {tb('read')}
                    </XtramButton>
                    <XtramButton variant="primary" onClick={handleCommit} disabled={!canCommit} busy={loading}>
                        {t('CreateAndImport')}
                    </XtramButton>
                </>
            )}
        >
            {tab === 'file' && renderFile()}
            {tab === 'data' && preview && (single ? renderSingle() : renderAll())}
            {tab === 'check' && preview && !single && renderCheck()}
            {tab === 'missing' && preview && renderMissing()}
            {tab === 'results' && results && renderResults()}
            {tab !== 'file' && error && <XtramNotice type="error">{error}</XtramNotice>}
        </XtramModal>
    );
};

export default AtzmaitBackupImportModal;
